import InfoIcon from "@mui/icons-material/Info";
import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import pluralize from "pluralize";
import { useContext, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useMSSP from "../../../../api/hooks/useMSSP";
import { msspDashboardKeys } from "../../../../api/queryKeys";
import CustomTooltip from "../../../../components/CustomTooltip";
import LoadingIcon from "../../../../components/LoadingIcon";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { DRAWER_TABS } from "../../../../utils/constants";
import { daysPastSince } from "../../../../utils/timestamp";
import ControlRemediationDrawer from "../../../posture/controls/components/ControlRemediationDrawer/ControlRemediationDrawer";
import RecentlyFoundLabel, {
  isRecentlyFound,
} from "../../../posture/controls/components/RecentlyFoundLabel";
import DetectionLabelIcon from "../../../posture/detections/components/DetectionLabelIcon";
import DetectionsTooltip from "../../../posture/detections/components/DetectionsTooltip";
import { getActiveDetections } from "../../../posture/detections/components/utils";
import ChipColor from "../../../remediation/plan/components/ChipColor";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";
import ImportanceCell from "../ImportanceCell";
const TopIssuesTable = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedControl, setSelectedControl] = useState(null);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const { switchTenant } = useMSSP();
  const [sortModel, setSortModel] = useState([
    {
      field: "severity",
      sort: "desc",
    },
    {
      field: "tenant_importance",
      sort: "desc",
    },
    {
      field: "affected_entities_count",
      sort: "desc",
    },
  ]);

  const colors = useColors();
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const { getMSSPTopIssues } = useAPI();

  const { data: topIssues, isLoading: isLoadingTopIssues } = useQuery({
    queryKey: msspDashboardKeys.topIssues(),
    queryFn: getMSSPTopIssues,
    select: (data) => data.data,
  });

  const columns = [
    {
      field: "control_id",
      headerName: "ID",
      width: 60,
      renderHeader: (params) => (
        <Typography
          variant="subtitle2"
          sx={{
            p: 1,
          }}
        >
          ID
        </Typography>
      ),
      renderCell: (params) => (
        <Typography
          variant="caption"
          sx={{
            p: 1,
          }}
        >{`#${params.value}`}</Typography>
      ),
    },
    {
      field: "name_for_gap_state",
      headerName: "Control Description",
      flex: 0.5,
      renderCell: (params) => {
        const maxSeverity = params.row?.last_detections?.length
          ? Math.max(
              ...params.row?.last_detections.map(
                (detection) => detection.severity
              )
            )
          : 0;
        return (
          <Box display="flex" flexDirection={"row"} gap={1}>
            {detectionsActive && params.row?.is_detected && (
              <Box display="flex" alignItems="center" width={"100%"}>
                <DetectionsTooltip
                  content={
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color={colors.common_black}
                        >
                          {getActiveDetections(params.row?.last_detections)
                            ?.length +
                            pluralize(
                              " Active event",
                              getActiveDetections(params.row?.last_detections)
                                ?.length
                            ) +
                            "."}
                        </Typography>
                        <Typography variant="caption">
                          Highest Severity:
                        </Typography>
                        <ChipColor
                          value={maxSeverity}
                          type={"controlPriority"}
                          size="small"
                          labelWidth={true}
                        />
                      </Box>
                      <Typography variant="caption">
                        Click the control to view details
                      </Typography>
                    </Box>
                  }
                >
                  <Box>
                    <DetectionLabelIcon sx={{ width: "22px" }} />
                  </Box>
                </DetectionsTooltip>
              </Box>
            )}
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {params.value}
              </Typography>
              {isRecentlyFound(params.row?.first_seen?.timestamp_utc) && (
                <RecentlyFoundLabel
                  foundTimestamp={params.row?.first_seen?.timestamp_utc || ""}
                />
              )}
            </Box>{" "}
          </Box>
        );
      },
    },
    {
      field: "tool",
      headerName: "Tool",
      width: 100,
      renderCell: (params) => (
        <CustomTooltip
          title={IntegrationsMapping[params.value?.toLowerCase()].displayName}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SvgToIcon
              IconLogo={IntegrationsMapping[params.value?.toLowerCase()]?.logo}
              width="40px"
            />
          </Box>
        </CustomTooltip>
      ),
    },
    {
      field: "severity",
      headerName: "Priority",
      width: 100,
      renderCell: (params) => (
        <ChipColor
          value={params.value}
          type={"controlPriority"}
          disabled={!params.row.is_gap}
        />
      ),
    },
    {
      field: "tenant_name",
      headerName: "Tenant",
      width: 100,
      renderCell: (params) => (
        <Typography variant="caption">{params.value}</Typography>
      ),
    },
    {
      field: "tenant_importance",
      headerName: "Importance",
      width: 140,
      renderCell: (params) => {
        return <ImportanceCell value={params.value} row={params.row} />;
      },
    },
    {
      field: "affected_entities_count",
      headerName: "Affected Entities",
      width: 170,
      sortable: true,
      valueGetter: (params) => params.value,
      disableClickEventBubbling: true,
      renderCell: (params) => {
        return <Typography variant="caption">{params.value}</Typography>;
      },
    },
    {
      field: "first_seen",
      headerName: "Days Open",
      width: 100,
      valueFormatter: (params) => {
        const daysPass = daysPastSince(
          params?.value?.timestamp_utc_human_readable
        );
        return Number(daysPass) || 0;
      },
      renderCell: (params) => {
        const daysPass = daysPastSince(
          params?.value?.timestamp_utc_human_readable
        );

        return (
          <Box style={{ whiteSpace: "nowrap" }}>{Number(daysPass) || 0}</Box>
        );
      },
    },
  ];

  const onRowClick = (control_id, tenant_id) => {
    const control = topIssues.find(
      (control) =>
        control.control_id === control_id && control.tenant_id === tenant_id
    );
    switchTenant(tenant_id, true);
    setSelectedControl(control);
    setOpenDrawer(true);
  };

  const resetSelection = () => {
    setSelectedControl(null);
    setOpenDrawer(false);
  };

  return (
    <Box
      sx={{
        boxShadow: "0px 0px 2px 1px " + colors.mssp_box_shadow_color,
        borderRadius: "8px",
        background: "#fff",
        minHeight: "200px",
      }}
    >
      <Box display="flex" alignItems="center" sx={{ p: 2, gap: 1 }}>
        <Typography variant="subtitle1">Top Issues</Typography>
        <CustomTooltip title="Lists top control issues aggregated from all environments.">
          <InfoIcon
            sx={{
              color: colors.tooltip_info_icon,
              width: "16px",
              height: "16px",
              cursor: "pointer",
            }}
          />
        </CustomTooltip>
      </Box>
      {isLoadingTopIssues ? (
        <LoadingIcon />
      ) : (
        <StyledDataGrid
          style={{
            cursor: "pointer",
            "& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type":
              {
                paddingLeft: "16px",
              },
            "& .MuiDataGrid-cell:last-of-type, & .MuiDataGrid-columnHeader:last-of-type":
              {
                paddingRight: "16px",
              },
          }}
          rows={topIssues}
          columns={columns}
          onCellClick={(params, event) => {
            if (params.field !== "__check__") {
              event.stopPropagation();
              const control_id = params.row.control_id;
              onRowClick(control_id, params.row.tenant_id);
              setSelectedTenantId(params.row.tenant_id);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          loading={isLoadingTopIssues}
          autoHeight
          getRowId={(row) => row.control_id + row.tenant_id}
          boxSXProps={{
            border: "none",
            width: "100%",
            ".MuiDataGrid-root": {
              border: "none",
              boxShadow: "none",
            },
          }}
          pageSizeOptionsOverride={[5, 10, 20, 50]}
        />
      )}
      {/* Drawer to show selected control details */}
      <ControlRemediationDrawer
        open={openDrawer}
        onClose={resetSelection}
        selectedTab={DRAWER_TABS.CONTROL_TAB}
        selectedControl={selectedControl}
      />
    </Box>
  );
};

export default TopIssuesTable;
